import { t, getLang, onLangChange } from './translations';
import { getCategoryLabel } from './categories';
import type { PoiData } from './types';

const popup   = document.getElementById('poi-popup')!;
const backdrop = document.getElementById('poi-popup-backdrop')!;
const photo = document.getElementById('popup-photo') as HTMLImageElement;
const nameEl = document.getElementById('popup-name')!;
const cat    = document.getElementById('popup-category')!;
const desc   = document.getElementById('popup-desc')!;
const link   = document.getElementById('popup-link') as HTMLAnchorElement;

const lightbox      = document.getElementById('photo-lightbox')!;
const lightboxPhoto = document.getElementById('lightbox-photo') as HTMLImageElement;

document.getElementById('popup-close')!.addEventListener('click', hide);
backdrop.addEventListener('click', hide);
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
        if (!lightbox.classList.contains('hidden')) hideLightbox();
        else if (!popup.classList.contains('hidden')) hide();
    }
});

photo.addEventListener('click', () => {
    if (photo.classList.contains('hidden') || !photo.src) return;
    lightboxPhoto.src = photo.src;
    lightboxPhoto.alt = photo.alt;
    lightbox.classList.remove('hidden');
    resetZoom();
});

document.getElementById('lightbox-close')!.addEventListener('click', hideLightbox);
lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) hideLightbox();
});

function hideLightbox(): void {
    lightbox.classList.add('hidden');
    lightboxPhoto.src = '';
    resetZoom();
}

// ── Lightbox zoom & pan (wheel, pinch, drag, double-click/tap) ──
const MIN_SCALE = 1;
const MAX_SCALE = 5;
let scale = 1;
let tx = 0, ty = 0;
const pointers = new Map<number, { x: number; y: number }>();
let pinchStartDist = 0;
let pinchStartScale = 1;
let dragging = false;
let dragStart = { x: 0, y: 0 };
let dragOrigin = { x: 0, y: 0 };
let lastTapTime = 0;

function applyTransform(): void {
    lightboxPhoto.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
}

function resetZoom(): void {
    scale = 1;
    tx = 0;
    ty = 0;
    applyTransform();
}

function clampPan(): void {
    const rect = lightboxPhoto.getBoundingClientRect();
    const baseW = rect.width / scale;
    const baseH = rect.height / scale;
    const maxX = Math.max(0, (baseW * scale - window.innerWidth) / 2);
    const maxY = Math.max(0, (baseH * scale - window.innerHeight) / 2);
    tx = Math.min(maxX, Math.max(-maxX, tx));
    ty = Math.min(maxY, Math.max(-maxY, ty));
}

function zoomAt(clientX: number, clientY: number, newScale: number): void {
    const oldScale = scale;
    scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale));

    const rect = lightbox.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;

    const pLocalX = (dx - tx) / oldScale;
    const pLocalY = (dy - ty) / oldScale;
    tx = dx - scale * pLocalX;
    ty = dy - scale * pLocalY;

    if (scale <= MIN_SCALE) {
        tx = 0;
        ty = 0;
    } else {
        clampPan();
    }
    applyTransform();
}

lightboxPhoto.addEventListener('wheel', (e) => {
    e.preventDefault();
    const factor = Math.exp(-e.deltaY * 0.0015);
    zoomAt(e.clientX, e.clientY, scale * factor);
}, { passive: false });

lightboxPhoto.addEventListener('dblclick', (e) => {
    e.preventDefault();
    zoomAt(e.clientX, e.clientY, scale > MIN_SCALE ? MIN_SCALE : 2.5);
});

lightboxPhoto.addEventListener('pointerdown', (e) => {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    lightboxPhoto.setPointerCapture(e.pointerId);

    if (pointers.size === 1) {
        dragging = scale > MIN_SCALE;
        dragStart = { x: e.clientX, y: e.clientY };
        dragOrigin = { x: tx, y: ty };
    } else if (pointers.size === 2) {
        dragging = false;
        const pts = [...pointers.values()];
        pinchStartDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        pinchStartScale = scale;
    }
});

lightboxPhoto.addEventListener('pointermove', (e) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.size === 2) {
        const pts = [...pointers.values()];
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        const midX = (pts[0].x + pts[1].x) / 2;
        const midY = (pts[0].y + pts[1].y) / 2;
        if (pinchStartDist > 0) {
            zoomAt(midX, midY, pinchStartScale * (dist / pinchStartDist));
        }
    } else if (dragging && pointers.size === 1) {
        tx = dragOrigin.x + (e.clientX - dragStart.x);
        ty = dragOrigin.y + (e.clientY - dragStart.y);
        clampPan();
        applyTransform();
    }
});

function endPointer(e: PointerEvent): void {
    pointers.delete(e.pointerId);
    if (lightboxPhoto.hasPointerCapture(e.pointerId)) {
        lightboxPhoto.releasePointerCapture(e.pointerId);
    }
    dragging = false;
    pinchStartDist = 0;

    if (pointers.size === 1) {
        const [p] = [...pointers.values()];
        dragging = scale > MIN_SCALE;
        dragStart = { x: p.x, y: p.y };
        dragOrigin = { x: tx, y: ty };
    }
}

lightboxPhoto.addEventListener('pointerup', endPointer);
lightboxPhoto.addEventListener('pointercancel', endPointer);

lightboxPhoto.addEventListener('touchend', (e) => {
    if (e.touches.length > 0) return;
    const now = Date.now();
    if (now - lastTapTime < 300) {
        const t = e.changedTouches[0];
        zoomAt(t.clientX, t.clientY, scale > MIN_SCALE ? MIN_SCALE : 2.5);
    }
    lastTapTime = now;
});

let _poi:  PoiData | null = null;

function _localName(poi: PoiData): string {
    const l = getLang();
    return (l === 'cs' ? poi.name : poi.name_en) || poi.name || poi.name_en || '';
}

function _localDesc(poi: PoiData): string {
    const l = getLang();
    return (l === 'cs' ? poi.description : poi.description_en) || poi.description || poi.description_en || '';
}

function _render(): void {
    if (!_poi) return;
    nameEl.textContent = _localName(_poi);
    cat.textContent    = getCategoryLabel(_poi.category, getLang());
    desc.textContent   = _localDesc(_poi) ?? '';

    if (_poi.photo_path) {
        photo.src = `/storage/${_poi.photo_path}`;
        photo.classList.remove('hidden');
        photo.onerror = () => photo.classList.add('hidden');
    } else {
        photo.classList.add('hidden');
    }

    if (_poi.url) {
        link.href        = _poi.url;
        link.textContent = t('moreInfo');
        link.classList.remove('hidden');
    } else {
        link.classList.add('hidden');
    }
}

onLangChange(() => { if (!popup.classList.contains('hidden')) _render(); });

export function show(poi: PoiData): void {
    _poi = poi;

    _render();
    popup.classList.remove('hidden');
    backdrop.classList.remove('hidden');
}

export function hide(): void {
    popup.classList.add('hidden');
    backdrop.classList.add('hidden');
    _poi = null;
}
